import { OMENS_ORIGIN } from './index'
import { OMENS_PAGES, OmensPageSeo } from './pages'

export interface ResolvedOmensPageSeo extends OmensPageSeo {
  path: string
  /** Absolute, no query, no trailing slash. */
  canonicalUrl: string
}

/** Hosts of the other brands served by this app: their pages never get Omens tags. */
const OTHER_BRAND_HOST = /souisha|medleve|cuidei|omens\.fit/

const pagePath = (pathname: string, asPath: string): string => {
  // Static routes: the route itself (a rewrite, e.g. Cuidei's "/" → /cuidei-hub, keeps its own pathname).
  if (!pathname.includes('[')) return pathname
  const path = asPath.split(/[?#]/, 1)[0].replace(/(.)\/+$/, '$1')
  return path || '/'
}

/**
 * The registry entry of the page being rendered, or undefined when it isn't an Omens page: not in the registry,
 * rendered on another brand's host (known in the browser only; static HTML is built once for every host), or a
 * page shared with the other brands whose server didn't see an Omens host.
 */
export const resolveOmensPageSeo = ({
  pathname,
  asPath,
  pageProps,
  hostname
}: {
  pathname: string
  asPath: string
  pageProps?: { omensHost?: unknown }
  hostname?: string
}): ResolvedOmensPageSeo | undefined => {
  const path = pagePath(pathname, asPath)
  const page = Object.prototype.hasOwnProperty.call(OMENS_PAGES, path) ? OMENS_PAGES[path] : undefined
  if (!page) return undefined
  if (page.hostScoped && pageProps?.omensHost !== true) return undefined
  if (hostname && OTHER_BRAND_HOST.test(hostname.toLowerCase())) return undefined
  const canonicalPath = page.canonical ?? path
  return { ...page, path, canonicalUrl: canonicalPath === '/' ? `${OMENS_ORIGIN}/` : `${OMENS_ORIGIN}${canonicalPath}` }
}

export const OMENS_ORGANIZATION_ID = `${OMENS_ORIGIN}/#organization`
export const OMENS_WEBSITE_ID = `${OMENS_ORIGIN}/#website`

/**
 * Reclame Aqui (PRD 2.7): reputation "Ótima", 100% of the complaints answered over the profile's 12-month window.
 * The window moves: update `OMENS_RECLAME_AQUI_PERIOD` once a month, every page reads it. No aggregateRating.
 */
export const OMENS_RECLAME_AQUI_PERIOD = { start: '2025-10-01', end: '2026-09-30' }
export const OMENS_RECLAME_AQUI_URL = 'https://www.reclameaqui.com.br/empresa/omens-servicos-online-e-software-ltda/'

/** Same `@id`s and values as the Programa Control v2 graph (PRD section 10), plus the Reclame Aqui profile. */
export const omensOrganizationNode = () => ({
  '@type': 'Organization',
  '@id': OMENS_ORGANIZATION_ID,
  name: 'Omens',
  url: `${OMENS_ORIGIN}/`,
  logo: { '@type': 'ImageObject', url: `${OMENS_ORIGIN}/omens-logo.svg`, width: 950, height: 160 },
  email: 'contato@omens.com.br',
  foundingDate: '2020',
  identifier: { '@type': 'PropertyValue', propertyID: 'CNPJ', value: '38.143.501/0001-08' },
  sameAs: [
    'https://www.instagram.com/omensbr/',
    'https://www.facebook.com/omensbrasil/',
    'https://www.linkedin.com/company/omensbr/',
    'https://www.youtube.com/channel/UCezMA48gBP6RrbXtwovouqA',
    'https://twitter.com/OmensBr',
    OMENS_RECLAME_AQUI_URL
  ]
})

export const omensWebsiteNode = () => ({
  '@type': 'WebSite',
  '@id': OMENS_WEBSITE_ID,
  url: `${OMENS_ORIGIN}/`,
  name: 'Omens',
  inLanguage: 'pt-BR',
  publisher: { '@id': OMENS_ORGANIZATION_ID }
})

/**
 * The site graph every Omens page carries (PRD 2.5): Organization + WebSite, and the page itself as a WebPage
 * when it is its own canonical (a duplicate describes nothing of its own).
 */
export const buildOmensSiteJsonLd = (seo: ResolvedOmensPageSeo) => ({
  '@context': 'https://schema.org',
  '@graph': [
    omensOrganizationNode(),
    omensWebsiteNode(),
    ...(seo.canonical
      ? []
      : [
          {
            '@type': 'WebPage',
            '@id': `${seo.canonicalUrl}#webpage`,
            url: seo.canonicalUrl,
            name: seo.title,
            description: seo.description,
            inLanguage: 'pt-BR',
            isPartOf: { '@id': OMENS_WEBSITE_ID },
            publisher: { '@id': OMENS_ORGANIZATION_ID }
          }
        ])
  ]
})
